// ============================================================
// THE COMPANY — RoleNotes Component
// ============================================================

import { useRef, useCallback, useEffect } from "react";
import DOMPurify from "dompurify";
import { useCompanyStore } from "../../store/useCompanyStore";
import RichTextEditor from "../shared/RichTextEditor";
import type { Role } from "../../lib/types";

interface RoleNotesProps {
  role: Role;
}

export default function RoleNotes({ role }: RoleNotesProps) {
  const updateRole = useCompanyStore((s) => s.updateRole);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (html: string) => {
      // Debounce saves to avoid excessive API calls
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        updateRole(role.id, { notes: html });
      }, 500);
    },
    [role.id, updateRole],
  );

  return (
    <section>
      <h3
        className="text-xs font-semibold uppercase tracking-wider mb-3"
        style={{ color: "var(--text-muted)" }}
      >
        Pinned Notes
      </h3>
      <RichTextEditor
        content={DOMPurify.sanitize(role.notes)}
        onChange={handleChange}
        placeholder="Add notes, reminders, or context for this role..."
      />
    </section>
  );
}
